import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';
import PasswordSecurityTab from './PasswordSecurityTab';

const GIT_BASE = String(API_BASE || '').replace(/\/api\/?$/, '');
const DEFAULT_PLUGIN_TERMS = "All sales are final. This product that the user uploaded is as-is. This is a direct payment from you to the plugin's author.";

function normalizeEmail(value) {
return String(value || '').trim().toLowerCase();
}

function downloadDurationLabel(months) {
if (Number(months) === 6) return '6 months';
if (Number(months) === 12) return '1 year';
return '---';
}

function itemSlug(value) {
return String(value || 'item').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'item';
}

function UserDashboard() {
const [activeTab, setActiveTab] = useState('profile');
const [user, setUser] = useState({
fullName: localStorage.getItem('userName') || '',
email: localStorage.getItem('userEmail') || '',
nickname: localStorage.getItem('userNickname') || ''
});
const [plugins, setPlugins] = useState([]);
const [workflows, setWorkflows] = useState([]);
const [orders, setOrders] = useState([]);
const [ordersLoading, setOrdersLoading] = useState(false);
const [ordersError, setOrdersError] = useState('');
const [contributing, setContributing] = useState({ plugins: [], workflows: [] });
const [contributingLoading, setContributingLoading] = useState(false);
const [contributingError, setContributingError] = useState('');
const [loading, setLoading] = useState(false);
const [pluginLoadError, setPluginLoadError] = useState('');
const [token] = useState(localStorage.getItem('token'));
const [uploadModal, setUploadModal] = useState(null);

useEffect(() => {
if (activeTab === 'plugins') {
loadUserPlugins();
}
if (activeTab === 'workflows') {
loadUserWorkflows();
}
if (activeTab === 'orders') {
loadOrders();
}
if (activeTab === 'contributing') {
loadContributing();
}
}, [activeTab]);

const loadUserPlugins = async () => {
setLoading(true);
setPluginLoadError('');
try {
const response = await axios.get(`${API_BASE}/plugin/mine`, {
headers: { Authorization: `Bearer ${token}` }
});
setPlugins(Array.isArray(response.data) ? response.data : []);
} catch (error) {
console.error('Error loading plugins:', error);
setPlugins([]);
setPluginLoadError(error.response?.data?.message || 'Unable to load your plugins. Please sign in again if this continues.');
}
setLoading(false);
};

const loadUserWorkflows = async () => {
setLoading(true);
try {
const userId = localStorage.getItem('userId');
const response = await axios.get(`${API_BASE}/workflow/user/${userId}`, {
headers: { Authorization: `Bearer ${token}` }
});
setWorkflows(response.data);
} catch (error) {
console.error('Error loading workflows:', error);
}
setLoading(false);
};

const loadOrders = async () => {
setOrdersLoading(true);
setOrdersError('');
try {
const response = await axios.get(`${API_BASE}/marketplace/orders`, {
headers: { Authorization: `Bearer ${token}` }
});
setOrders(Array.isArray(response.data) ? response.data : []);
} catch (error) {
setOrders([]);
setOrdersError(error.response?.data?.message || 'Unable to load your orders.');
}
setOrdersLoading(false);
};

const loadContributing = async () => {
setContributingLoading(true);
setContributingError('');
try {
  const response = await axios.get(`${API_BASE}/account/contributing`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  setContributing({
    plugins: Array.isArray(response.data?.plugins || response.data?.Plugins) ? (response.data.plugins || response.data.Plugins) : [],
    workflows: Array.isArray(response.data?.workflows || response.data?.Workflows) ? (response.data.workflows || response.data.Workflows) : []
  });
} catch (error) {
  setContributing({ plugins: [], workflows: [] });
  setContributingError(error.response?.data?.message || error.response?.data?.Message || 'Unable to load your contributing repositories.');
} finally {
  setContributingLoading(false);
}
};

const updateProfile = async (updatedData) => {
try {
await axios.put(`${API_BASE}/account/profile`, updatedData, {
headers: { Authorization: `Bearer ${token}` }
});

  localStorage.setItem('userName', updatedData.fullName);
  localStorage.setItem('userEmail', updatedData.email);
  localStorage.setItem('userNickname', updatedData.nickname || '');
  setUser(updatedData);
  alert('Profile updated successfully!');
} catch (error) {
  alert('Failed to update profile');
}

};

const deletePlugin = async (pluginId) => {
if (!window.confirm('Are you sure you want to delete this plugin? This action cannot be undone.')) {
return;
}

try {
  await axios.delete(`${API_BASE}/plugin/${pluginId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  alert('Plugin deleted successfully');
  loadUserPlugins();
} catch (error) {
  alert('Failed to delete plugin');
}

};

const deleteWorkflow = async (workflowId) => {
if (!window.confirm('Are you sure you want to delete this workflow package? This action cannot be undone.')) {
return;
}

try {
  await axios.delete(`${API_BASE}/workflow/${workflowId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  alert('Workflow deleted successfully');
  loadUserWorkflows();
} catch (error) {
  alert('Failed to delete workflow');
}

};

const logout = () => {
localStorage.clear();
window.location.href = '/';
};

return (
<div style={styles.container}>
<nav style={styles.navbar}>
<a href="/" style={styles.logoLink} aria-label="Go to Plugin Repository homepage">
<h1 style={styles.logo}>Plugin Repository</h1>
</a>
<div style={styles.navRight}>
<span style={styles.userName}>{user.nickname || user.fullName}</span>
<button onClick={logout} style={styles.logoutBtn}>Logout</button>
</div>
</nav>

  <div style={styles.content}>
    <aside style={styles.sidebar}>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'profile' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('profile')}
      >
        My Profile
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'plugins' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('plugins')}
      >
        My Plugins
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'workflows' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('workflows')}
      >
        My Workflows
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'orders' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('orders')}
      >
        Orders
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'contributing' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('contributing')}
      >
        Contributing
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'payments' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('payments')}
      >
        Payment Settings
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'security' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('security')}
      >
        Change Password
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'upload' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('upload')}
      >
        Upload Plugin
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'workflowUpload' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('workflowUpload')}
      >
        Upload Workflow
      </button>
      <button
        style={{...styles.sidebarBtn, ...(activeTab === 'tutorial' ? styles.activeSidebarBtn : {})}}
        onClick={() => setActiveTab('tutorial')}
      >
        Tutorial
      </button>
    </aside>

    <main style={styles.main}>
      {activeTab === 'profile' && (
        <ProfileTab user={user} onUpdate={updateProfile} />
      )}
      {activeTab === 'plugins' && (
        <PluginsTab 
          plugins={plugins} 
          loading={loading} 
          onDelete={deletePlugin}
          onRefresh={loadUserPlugins}
          token={token}
          loadError={pluginLoadError}
          onUpload={() => setUploadModal('plugin')}
        />
      )}
      {activeTab === 'workflows' && (
        <WorkflowsTab 
          workflows={workflows} 
          loading={loading} 
          onDelete={deleteWorkflow}
          onRefresh={loadUserWorkflows}
          token={token}
          onUpload={() => setUploadModal('workflow')}
        />
      )}
      {activeTab === 'orders' && (
        <OrdersTab orders={orders} loading={ordersLoading} error={ordersError} onRefresh={loadOrders} />
      )}
      {activeTab === 'contributing' && (
        <ContributingTab
          contributing={contributing}
          loading={contributingLoading}
          error={contributingError}
          token={token}
          onRefresh={loadContributing}
        />
      )}
      {activeTab === 'upload' && (
        <UploadTab token={token} onSuccess={loadUserPlugins} />
      )}
      {activeTab === 'payments' && <PaymentSettingsTab token={token} />}
      {activeTab === 'security' && <PasswordSecurityTab token={token} />}
      {activeTab === 'workflowUpload' && (
        <WorkflowUploadTab token={token} onSuccess={loadUserWorkflows} />
      )}
      {activeTab === 'tutorial' && <TutorialTab />}
    </main>
  </div>

  {uploadModal && (
    <div style={styles.modalOverlay} role="presentation" onMouseDown={() => setUploadModal(null)}>
      <div
        style={styles.uploadModal}
        role="dialog"
        aria-modal="true"
        aria-label={uploadModal === 'plugin' ? 'Upload Plugin' : 'Upload Workflow'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" onClick={() => setUploadModal(null)} aria-label="Close upload window" style={styles.modalCloseBtn}>×</button>
        {uploadModal === 'plugin' ? (
          <UploadTab token={token} onSuccess={async () => {
            setUploadModal(null);
            setActiveTab('plugins');
            await loadUserPlugins();
          }} />
        ) : (
          <WorkflowUploadTab token={token} onSuccess={async () => {
            setUploadModal(null);
            setActiveTab('workflows');
            await loadUserWorkflows();
          }} />
        )}
      </div>
    </div>
  )}
</div>

);
}

function ContributingTab({ contributing, loading, error, token, onRefresh }) {
const [status, setStatus] = useState('');
const [removing, setRemoving] = useState('');
const items = [
  ...(contributing.plugins || []).map(item => ({ ...item, type: 'plugin' })),
  ...(contributing.workflows || []).map(item => ({ ...item, type: 'workflow' }))
];

const removeContribution = async (item) => {
if (!window.confirm(`Remove yourself from ${item.name}? You will no longer be able to push to this repository.`)) return;
setRemoving(`${item.type}-${item.id}`);
setStatus('');
try {
  await axios.delete(`${API_BASE}/account/contributing/${item.type}/${item.id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  setStatus(`You have been removed from ${item.name}.`);
  await onRefresh();
} catch (error) {
  setStatus(error.response?.data?.message || error.response?.data?.Message || 'Unable to remove repository access.');
} finally {
  setRemoving('');
}
};

return (
<div style={styles.tabContent}>
  <h2 style={styles.sectionTitle}>Contributing</h2>
  <p style={styles.sectionDescription}>Repositories where another author has granted your plugins.gotchat.ai account Git access.</p>
  {loading && <p>Loading contributing repositories...</p>}
  {error && <div style={styles.errorMessage}>{error}</div>}
  {status && <div style={styles.successMessage}>{status}</div>}
  {!loading && !error && items.length === 0 && <div style={styles.emptyState}>You are not currently assigned to any plugin or workflow repository.</div>}
  <div style={styles.contributingList}>
    {items.map(item => (
      <article key={`${item.type}-${item.id}`} style={styles.contributingCard}>
        <div style={styles.contributingHeader}>
          <div>
            <span style={styles.contributingType}>{item.type}</span>
            <h3 style={styles.contributingName}>{item.name}</h3>
          </div>
          <button
            type="button"
            onClick={() => removeContribution(item)}
            disabled={removing === `${item.type}-${item.id}`}
            style={styles.removeContributionBtn}
          >
            {removing === `${item.type}-${item.id}` ? 'Removing…' : 'Remove myself'}
          </button>
        </div>
        {item.description && <p style={styles.contributingDescription}>{item.description}</p>}
        {item.gitUrl && <code style={styles.contributingUrl}>{item.gitUrl}</code>}
        <p style={styles.contributingHint}>Use your plugins.gotchat.ai login email and password for Git authentication.</p>
      </article>
    ))}
  </div>
</div>
);
}

function OrdersTab({ orders, loading, error, onRefresh }) {
return (
<div style={styles.tabContent}>
  <div style={styles.tabHeader}>
    <div>
      <h2 style={{marginBottom: '6px'}}>Orders</h2>
      <p style={{marginTop: 0, color: '#64748b'}}>Plugins and workflows you have purchased.</p>
    </div>
    <button type="button" onClick={onRefresh} style={styles.refreshBtn}>Refresh</button>
  </div>
  {loading ? <p>Loading orders...</p> : error ? <div style={styles.errorBox}>{error}</div> : orders.length === 0 ? (
    <div style={styles.emptyState}>You have not purchased any plugins or workflows yet.</div>
  ) : (
    <div style={styles.ordersList}>
      {orders.map(order => {
        const detailUrl = `/${order.itemType}/${itemSlug(order.itemName)}/${order.itemId}`;
        const expiresAt = order.downloadAccessExpiresAt ? new Date(order.downloadAccessExpiresAt) : null;
        return <article key={`${order.itemType}-${order.id}`} style={styles.orderCard}>
          <div style={styles.orderMain}>
            <div style={styles.orderType}>{order.itemType}</div>
            <a href={detailUrl} style={styles.orderTitle}>{order.itemName}</a>
            <div style={styles.orderMeta}>Purchased {new Date(order.purchasedAt).toLocaleString()}</div>
            <div style={styles.orderMeta}>Download duration: {downloadDurationLabel(order.downloadAccessDurationMonths)}</div>
            <div style={styles.orderMeta}>{expiresAt ? `Access ${order.hasDownloadAccess ? 'ends' : 'ended'} ${expiresAt.toLocaleString()}` : 'Download access has no duration limit.'}</div>
          </div>
          <div style={styles.orderSummary}>
            <strong>{order.currency || 'USD'} {Number(order.amount || 0).toFixed(2)}</strong>
            <span style={{...styles.orderStatus, ...(order.hasDownloadAccess ? styles.orderStatusActive : styles.orderStatusExpired)}}>
              {order.hasDownloadAccess ? 'Active' : String(order.status || 'expired')}
            </span>
            {order.hasDownloadAccess && <a href={detailUrl} style={styles.smallActionBtn}>View & Download</a>}
          </div>
        </article>;
      })}
    </div>
  )}
</div>
);
}

function PaymentSettingsTab({ token }) {
const [apiKey, setApiKey] = useState('');
const [ipnSecret, setIpnSecret] = useState('');
const [account, setAccount] = useState(null);
const [enabled, setEnabled] = useState(true);
const [referralUrl, setReferralUrl] = useState('');
const [saving, setSaving] = useState(false);
const [message, setMessage] = useState('');

useEffect(() => {
  const load = async () => {
    try {
      const [accountsResponse, providersResponse] = await Promise.all([
        axios.get(`${API_BASE}/marketplace/seller/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/marketplace/providers`)
      ]);
      const nowPayments = (accountsResponse.data?.accounts || []).find(item => item.provider === 'nowpayments');
      const provider = (providersResponse.data?.providers || []).find(item => item.id === 'nowpayments');
      setAccount(nowPayments || null);
      setEnabled(Boolean(provider?.enabled));
      setReferralUrl(providersResponse.data?.nowPaymentsReferralUrl || '');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to load payment settings.');
    }
  };
  load();
}, [token]);

const save = async (event) => {
  event.preventDefault();
  setSaving(true);
  setMessage('');
  try {
    const response = await axios.put(`${API_BASE}/marketplace/seller/nowpayments`, {
      apiKey,
      ipnSecret
    }, { headers: { Authorization: `Bearer ${token}` } });
    setAccount(response.data);
    setApiKey(''); setIpnSecret('');
    setMessage(response.data?.message || 'NOWPayments account saved.');
  } catch (error) {
    setMessage(error.response?.data?.message || 'Unable to verify the NOWPayments account.');
  }
  setSaving(false);
};

return (
<div style={styles.tabContent}>
  <h2>Payment Settings</h2>
  <p>NOWPayments is the default provider for cryptocurrency sales. You can now earn from your unique plugins.</p>
  {!account && <div style={styles.referralCard}>
    <strong>Need a NOWPayments account?</strong>
    <p style={styles.referralText}>Create an individual account accepting cryptocurrency. This is the quickest way to accept funds directly into your cryptocurrency wallet.</p>
    {referralUrl && <a href={referralUrl} target="_blank" rel="noopener noreferrer" style={styles.referralBtn}>Create NOWPayments Account</a>}
  </div>}
  <div style={styles.infoBox}>
    In your NOWPayments account, add your BTC or ETH outcome wallet, create an API key, and generate an IPN secret. Payments created here belong to your NOWPayments account and settle using your wallet settings. Secrets are encrypted and are never returned to the browser.
  </div>
  {!enabled && <div style={styles.gitAccessStatus}>NOWPayments is currently disabled by the administrator.</div>}
  <form onSubmit={save} style={styles.profileForm}>
    <div style={styles.formGroup}>
      <label style={styles.label}>NOWPayments API Key</label>
      <input type="password" autoComplete="off" value={apiKey} onChange={(event) => setApiKey(event.target.value)} maxLength={500} style={styles.input} placeholder={account ? 'Enter a new key to replace the saved key' : 'API key'} />
    </div>
    <div style={styles.formGroup}>
      <label style={styles.label}>NOWPayments IPN Secret</label>
      <input type="password" autoComplete="off" value={ipnSecret} onChange={(event) => setIpnSecret(event.target.value)} maxLength={500} style={styles.input} placeholder="IPN secret (shown once by NOWPayments)" />
    </div>
    <button type="submit" disabled={saving || !enabled || !apiKey.trim() || !ipnSecret.trim()} style={{...styles.primaryBtn, opacity: saving || !enabled ? 0.6 : 1}}>
      {saving ? 'Verifying...' : 'Save and Verify NOWPayments'}
    </button>
  </form>
  {account && <p><strong>Status:</strong> {account.status || 'pending'} · {account.externalMerchantId}</p>}
  {message && <div style={styles.gitAccessStatus}>{message}</div>}
</div>
);
}

function ProfileTab({ user, onUpdate }) {
const [formData, setFormData] = useState(user);
const [editing, setEditing] = useState(false);

const handleSubmit = (e) => {
e.preventDefault();
onUpdate(formData);
setEditing(false);
};

return (
<div style={styles.tabContent}>
<h2>My Profile</h2>

  <form onSubmit={handleSubmit} style={styles.profileForm}>
    <div style={styles.formGroup}>
      <label style={styles.label}>Full Name</label>
      <input
        type="text"
        value={formData.fullName}
        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
        onFocus={() => setEditing(true)}
        readOnly={!editing}
        style={{...styles.input, ...(editing ? null : styles.inputReadOnly)}}
      />
    </div>

    <div style={styles.formGroup}>
      <label style={styles.label}>Nickname (optional)</label>
      <input
        type="text"
        value={formData.nickname || ''}
        onChange={(e) => setFormData({...formData, nickname: e.target.value})}
        onFocus={() => setEditing(true)}
        readOnly={!editing}
        style={{...styles.input, ...(editing ? null : styles.inputReadOnly)}}
        placeholder="Choose a unique nickname"
      />
    </div>

    <div style={styles.formGroup}>
      <label style={styles.label}>Email Address</label>
      <input
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({...formData, email: e.target.value})}
        onFocus={() => setEditing(true)}
        readOnly={!editing}
        style={{...styles.input, ...(editing ? null : styles.inputReadOnly)}}
      />
    </div>

    <div style={styles.formActions}>
      {editing ? (
        <>
          <button type="submit" style={styles.primaryBtn}>Save Changes</button>
          <button type="button" onClick={() => setEditing(false)} style={styles.secondaryBtn}>
            Cancel
          </button>
        </>
      ) : (
        <button type="button" onClick={() => setEditing(true)} style={styles.primaryBtn}>
          Edit Profile
        </button>
      )}
    </div>
  </form>
</div>

);
}

function PluginsTab({ plugins, loading, onDelete, onRefresh, token, loadError, onUpload }) {
const [paymentOptions, setPaymentOptions] = useState({ providers: [], payCurrencies: ['btc', 'eth'], defaultProvider: 'nowpayments', defaultPayCurrency: 'btc' });

useEffect(() => {
axios.get(`${API_BASE}/marketplace/providers`).then(response => {
setPaymentOptions(response.data || {});
}).catch(() => {});
}, []);

if (loading) {
return <div style={styles.loading}>Loading your plugins...</div>;
}

const getStatusBadge = (status) => {
const colors = {
Pending: '#f39c12',
Approved: '#27ae60',
Rejected: '#e74c3c'
};
return (
<span style={{...styles.badge, background: colors[status] || '#95a5a6'}}>
{status}
</span>
);
};

return (
<div style={styles.tabContent}>
<div style={styles.tabHeader}>
<h2>My Plugins</h2>
<div style={styles.headerActions}>
<button onClick={onUpload} style={styles.uploadShortcutBtn}>Upload</button>
<button onClick={onRefresh} style={styles.refreshBtn}>🔄 Refresh</button>
</div>
</div>

  {loadError && <div style={styles.gitAccessStatus}>{loadError}</div>}

  {!loadError && (plugins.length === 0 ? (
    <div style={styles.emptyState}>
      <p>You haven't uploaded any plugins yet.</p>
      <p>Click "Upload Plugin" to get started!</p>
    </div>
  ) : (
    <div style={styles.pluginsList}>
      {plugins.map(plugin => (
        <div key={plugin.id} style={styles.pluginItem}>
          <div style={styles.pluginInfo}>
            <h3>{plugin.name}</h3>
            <p style={styles.pluginDesc}>{plugin.description}</p>
            <div style={styles.pluginMeta}>
              <span>Version: {plugin.version}</span>
              <span>Downloads: {plugin.downloads}</span>
              <span>Repo: <code style={styles.repoSlug}>{plugin.repoSlug}</code></span>
              <span>Rating: ⭐ {plugin.averageRating.toFixed(1)}</span>
            </div>
            <PluginSalesSettingsCard plugin={plugin} token={token} paymentOptions={paymentOptions} onSaved={onRefresh} />
            <PluginGitAccessCard plugin={plugin} token={token} onSaved={onRefresh} />
          </div>
          <div style={styles.pluginActions}>
            {getStatusBadge(plugin.status)}
            <button
              onClick={() => onDelete(plugin.id)}
              style={styles.deleteBtn}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  ))}
</div>

);
}

function PluginSalesSettingsCard({ plugin, token, paymentOptions, onSaved }) {
const [editing, setEditing] = useState(false);
const [isPaid, setIsPaid] = useState(Boolean(plugin.isPaid));
const [price, setPrice] = useState(plugin.isPaid ? String(plugin.price || '') : '');
const [paymentProvider, setPaymentProvider] = useState(plugin.paymentProvider || paymentOptions.defaultProvider || 'nowpayments');
const [payCurrency, setPayCurrency] = useState(plugin.payCurrency || paymentOptions.defaultPayCurrency || 'btc');
const [termsOfSale, setTermsOfSale] = useState(plugin.termsOfSale || DEFAULT_PLUGIN_TERMS);
const [licenseAgreement, setLicenseAgreement] = useState(plugin.licenseAgreement || '');
const [downloadAccessDurationMonths, setDownloadAccessDurationMonths] = useState(plugin.downloadAccessDurationMonths == null ? '' : String(plugin.downloadAccessDurationMonths));
const [saving, setSaving] = useState(false);
const [message, setMessage] = useState('');

useEffect(() => {
setIsPaid(Boolean(plugin.isPaid));
setPrice(plugin.isPaid ? String(plugin.price || '') : '');
setPaymentProvider(plugin.paymentProvider || paymentOptions.defaultProvider || 'nowpayments');
setPayCurrency(plugin.payCurrency || paymentOptions.defaultPayCurrency || 'btc');
setTermsOfSale(plugin.termsOfSale || DEFAULT_PLUGIN_TERMS);
setLicenseAgreement(plugin.licenseAgreement || '');
setDownloadAccessDurationMonths(plugin.downloadAccessDurationMonths == null ? '' : String(plugin.downloadAccessDurationMonths));
}, [plugin, paymentOptions.defaultProvider, paymentOptions.defaultPayCurrency]);

const save = async (event) => {
event.preventDefault();
setSaving(true);
setMessage('');
try {
const response = await axios.put(`${API_BASE}/plugin/${plugin.id}/sales-settings`, {
isPaid,
price: isPaid ? Number(price) : 0,
currency: 'USD',
paymentProvider,
payCurrency,
termsOfSale: termsOfSale || DEFAULT_PLUGIN_TERMS,
licenseAgreement,
downloadAccessDurationMonths: downloadAccessDurationMonths === '' ? null : Number(downloadAccessDurationMonths)
}, { headers: { Authorization: `Bearer ${token}` } });
setMessage(response.data?.message || 'Sales settings saved.');
setEditing(false);
if (onSaved) await onSaved();
} catch (error) {
setMessage(error.response?.data?.message || 'Unable to save sales settings.');
}
setSaving(false);
};

const enabledProviders = (paymentOptions.providers || []).filter(item => item.enabled && item.configured);
const selectableProviders = enabledProviders.some(item => item.id === paymentProvider)
? enabledProviders
: [{ id: paymentProvider, name: paymentProvider === 'nowpayments' ? 'NOWPayments' : paymentProvider }, ...enabledProviders];
const providerLabel = (paymentOptions.providers || []).find(item => item.id === plugin.paymentProvider)?.name
|| (plugin.paymentProvider === 'nowpayments' ? 'NOWPayments' : plugin.paymentProvider || 'payment provider');

return (
<div style={styles.salesCard}>
<div style={styles.salesHeader}>
<div>
<div style={styles.gitAccessTitle}>Sales Settings</div>
<div style={styles.gitAccessText}>
{plugin.isPaid ? `Paid · ${plugin.currency || 'USD'} ${Number(plugin.price).toFixed(2)} · ${String(plugin.payCurrency || '').toUpperCase()} via ${providerLabel} · Download duration: ${downloadDurationLabel(plugin.downloadAccessDurationMonths)}` : 'Free download'}
</div>
</div>
<button type="button" onClick={() => setEditing(value => !value)} style={styles.smallActionBtn}>
{editing ? 'Cancel' : 'Edit Price'}
</button>
</div>
{editing && <form onSubmit={save} style={styles.salesForm}>
<label style={styles.label}>
<input type="checkbox" checked={isPaid} onChange={(event) => setIsPaid(event.target.checked)} />{' '}
Sell this plugin
</label>
{isPaid && <>
<div style={styles.salesGrid}>
<div>
<label style={styles.label}>Price (USD)</label>
<input type="number" min="0.50" max="10000" step="0.01" required value={price} onChange={(event) => setPrice(event.target.value)} style={styles.input} />
</div>
<div>
<label style={styles.label}>Payment Provider</label>
<select value={paymentProvider} onChange={(event) => setPaymentProvider(event.target.value)} style={styles.select}>
{selectableProviders.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
</select>
</div>
<div>
<label style={styles.label}>Buyer Pays With</label>
<select value={payCurrency} onChange={(event) => setPayCurrency(event.target.value)} style={styles.select}>
{(paymentOptions.payCurrencies || []).map(currency => <option key={currency} value={currency}>{currency.toUpperCase()}</option>)}
</select>
</div>
<div>
<label style={styles.label}>Download Duration</label>
<select value={downloadAccessDurationMonths} onChange={(event) => setDownloadAccessDurationMonths(event.target.value)} style={styles.select}>
<option value="">---</option>
<option value="6">6 months</option>
<option value="12">1 year</option>
</select>
</div>
</div>
<div style={styles.formGroup}>
<label style={styles.label}>Terms of Sale</label>
<textarea rows={4} value={termsOfSale} onChange={(event) => setTermsOfSale(event.target.value)} style={styles.textarea} />
<button type="button" onClick={() => setTermsOfSale(DEFAULT_PLUGIN_TERMS)} style={styles.secondaryBtn}>Use Default Terms</button>
</div>
<div style={styles.formGroup}>
<label style={styles.label}>Plugin License Agreement</label>
<textarea rows={5} value={licenseAgreement} onChange={(event) => setLicenseAgreement(event.target.value)} style={styles.textarea} placeholder="Optional license terms the buyer must accept." />
</div>
</>}
<button type="submit" disabled={saving} style={{...styles.primaryBtn, opacity: saving ? 0.6 : 1}}>{saving ? 'Saving...' : 'Save Sales Settings'}</button>
</form>}
{message && <div style={styles.gitAccessStatus}>{message}</div>}
</div>
);
}

function WorkflowsTab({ workflows, loading, onDelete, onRefresh, token, onUpload }) {
const [paymentOptions, setPaymentOptions] = useState({ providers: [], payCurrencies: ['btc', 'eth'], defaultProvider: 'nowpayments', defaultPayCurrency: 'btc' });

useEffect(() => {
axios.get(`${API_BASE}/marketplace/providers`).then(response => setPaymentOptions(response.data || {})).catch(() => {});
}, []);

if (loading) {
return <div style={styles.loading}>Loading your workflows...</div>;
}

const getStatusBadge = (status) => {
const colors = {
Pending: '#f39c12',
Approved: '#27ae60',
Rejected: '#e74c3c'
};
return (
<span style={{...styles.badge, background: colors[status] || '#95a5a6'}}>
{status}
</span>
);
};

return (
<div style={styles.tabContent}>
<div style={styles.tabHeader}>
<h2>My Workflows</h2>
<div style={styles.headerActions}>
<button onClick={onUpload} style={styles.uploadShortcutBtn}>Upload</button>
<button onClick={onRefresh} style={styles.refreshBtn}>Refresh</button>
</div>
</div>

  {workflows.length === 0 ? (
    <div style={styles.emptyState}>
      <p>You haven't uploaded any workflows yet.</p>
      <p>Click "Upload Workflow" to get started.</p>
    </div>
  ) : (
    <div style={styles.pluginsList}>
      {workflows.map(workflow => (
        <div key={workflow.id} style={styles.pluginItem}>
          <div style={styles.pluginInfo}>
            <h3>{workflow.name}</h3>
            <p style={styles.pluginDesc}>{workflow.description}</p>
            <div style={styles.pluginMeta}>
              <span>Version: {workflow.version}</span>
              <span>Downloads: {workflow.downloads}</span>
              <span>Repo: <code style={styles.repoSlug}>{workflow.repoSlug}</code></span>
            </div>
            <WorkflowSalesSettingsCard workflow={workflow} token={token} paymentOptions={paymentOptions} onSaved={onRefresh} />
            <WorkflowGitAccessCard workflow={workflow} token={token} onSaved={onRefresh} />
          </div>
          <div style={styles.pluginActions}>
            {getStatusBadge(workflow.status)}
            <button
              onClick={() => onDelete(workflow.id)}
              style={styles.deleteBtn}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  )}
</div>

);
}

function WorkflowSalesSettingsCard({ workflow, token, paymentOptions, onSaved }) {
const [editing, setEditing] = useState(false);
const [isPaid, setIsPaid] = useState(Boolean(workflow.isPaid));
const [price, setPrice] = useState(workflow.isPaid ? String(workflow.price || '') : '');
const [paymentProvider, setPaymentProvider] = useState(workflow.paymentProvider || paymentOptions.defaultProvider || 'nowpayments');
const [payCurrency, setPayCurrency] = useState(workflow.payCurrency || paymentOptions.defaultPayCurrency || 'btc');
const [termsOfSale, setTermsOfSale] = useState(workflow.termsOfSale || DEFAULT_PLUGIN_TERMS);
const [licenseAgreement, setLicenseAgreement] = useState(workflow.licenseAgreement || '');
const [downloadAccessDurationMonths, setDownloadAccessDurationMonths] = useState(workflow.downloadAccessDurationMonths == null ? '' : String(workflow.downloadAccessDurationMonths));
const [saving, setSaving] = useState(false);
const [message, setMessage] = useState('');

useEffect(() => {
setIsPaid(Boolean(workflow.isPaid)); setPrice(workflow.isPaid ? String(workflow.price || '') : '');
setPaymentProvider(workflow.paymentProvider || paymentOptions.defaultProvider || 'nowpayments');
setPayCurrency(workflow.payCurrency || paymentOptions.defaultPayCurrency || 'btc');
setTermsOfSale(workflow.termsOfSale || DEFAULT_PLUGIN_TERMS); setLicenseAgreement(workflow.licenseAgreement || '');
setDownloadAccessDurationMonths(workflow.downloadAccessDurationMonths == null ? '' : String(workflow.downloadAccessDurationMonths));
}, [workflow, paymentOptions.defaultProvider, paymentOptions.defaultPayCurrency]);

const save = async event => {
event.preventDefault(); setSaving(true); setMessage('');
try {
const response = await axios.put(`${API_BASE}/workflow/${workflow.id}/sales-settings`, { isPaid, price: isPaid ? Number(price) : 0, currency: 'USD', paymentProvider, payCurrency, termsOfSale: termsOfSale || DEFAULT_PLUGIN_TERMS, licenseAgreement, downloadAccessDurationMonths: downloadAccessDurationMonths === '' ? null : Number(downloadAccessDurationMonths) }, { headers: { Authorization: `Bearer ${token}` } });
setMessage(response.data?.message || 'Sales settings saved.'); setEditing(false); if (onSaved) await onSaved();
} catch (error) { setMessage(error.response?.data?.message || 'Unable to save sales settings.'); }
setSaving(false);
};
const enabledProviders = (paymentOptions.providers || []).filter(item => item.enabled && item.configured);
const selectableProviders = enabledProviders.some(item => item.id === paymentProvider) ? enabledProviders : [{ id: paymentProvider, name: paymentProvider === 'nowpayments' ? 'NOWPayments' : paymentProvider }, ...enabledProviders];
return <div style={styles.salesCard}><div style={styles.salesHeader}><div><div style={styles.gitAccessTitle}>Sales Settings</div><div style={styles.gitAccessText}>{workflow.isPaid ? `Paid · ${workflow.currency || 'USD'} ${Number(workflow.price).toFixed(2)} · ${String(workflow.payCurrency || '').toUpperCase()} · Download duration: ${downloadDurationLabel(workflow.downloadAccessDurationMonths)}` : 'Free download'}</div></div><button type="button" onClick={() => setEditing(value => !value)} style={styles.smallActionBtn}>{editing ? 'Cancel' : 'Edit Price'}</button></div>{editing && <form onSubmit={save} style={styles.salesForm}><label style={styles.label}><input type="checkbox" checked={isPaid} onChange={event => setIsPaid(event.target.checked)} /> Sell this workflow</label>{isPaid && <><div style={styles.salesGrid}><div><label style={styles.label}>Price (USD)</label><input type="number" min="0.50" max="10000" step="0.01" required value={price} onChange={event => setPrice(event.target.value)} style={styles.input} /></div><div><label style={styles.label}>Payment Provider</label><select value={paymentProvider} onChange={event => setPaymentProvider(event.target.value)} style={styles.select}>{selectableProviders.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div><div><label style={styles.label}>Buyer Pays With</label><select value={payCurrency} onChange={event => setPayCurrency(event.target.value)} style={styles.select}>{(paymentOptions.payCurrencies || []).map(currency => <option key={currency} value={currency}>{currency.toUpperCase()}</option>)}</select></div><div><label style={styles.label}>Download Duration</label><select value={downloadAccessDurationMonths} onChange={event => setDownloadAccessDurationMonths(event.target.value)} style={styles.select}><option value="">---</option><option value="6">6 months</option><option value="12">1 year</option></select></div></div><div style={styles.formGroup}><label style={styles.label}>Terms of Sale</label><textarea rows={4} value={termsOfSale} onChange={event => setTermsOfSale(event.target.value)} style={styles.textarea} /><button type="button" onClick={() => setTermsOfSale(DEFAULT_PLUGIN_TERMS)} style={styles.secondaryBtn}>Use Default Terms</button></div><div style={styles.formGroup}><label style={styles.label}>Workflow License Agreement</label><textarea rows={5} value={licenseAgreement} onChange={event => setLicenseAgreement(event.target.value)} style={styles.textarea} placeholder="Optional license terms buyers must accept." /></div></>}<button type="submit" disabled={saving} style={styles.primaryBtn}>{saving ? 'Saving...' : 'Save Sales Settings'}</button></form>}{message && <div style={styles.gitAccessStatus}>{message}</div>}</div>;
}

function PluginGitAccessCard({ plugin, token, onSaved }) {
return (
  <GitAccessCard
    title="Plugin Git Access"
    helperText="Allow other existing users to clone, pull, and push to this plugin repo with their own login."
    repoSlug={plugin.repoSlug}
    allowedGitEmails={plugin.allowedGitEmails}
    updatedBy={plugin.gitAccessUpdatedBy}
    updatedAt={plugin.gitAccessUpdatedAt}
    endpoint={`${API_BASE}/plugin/${plugin.id}/git-access`}
    emptyText="Only you can access this plugin repo right now."
    token={token}
    onSaved={onSaved}
  />
);
}

function WorkflowGitAccessCard({ workflow, token, onSaved }) {
return (
  <GitAccessCard
    title="Workflow Git Access"
    helperText="Allow other existing users to clone, pull, and push to this workflow repo with their own login."
    repoSlug={workflow.repoSlug}
    allowedGitEmails={workflow.allowedGitEmails}
    updatedBy={workflow.gitAccessUpdatedBy}
    updatedAt={workflow.gitAccessUpdatedAt}
    endpoint={`${API_BASE}/workflow/${workflow.id}/git-access`}
    emptyText="Only you can access this workflow repo right now."
    token={token}
    onSaved={onSaved}
  />
);
}

function GitAccessCard({ title, helperText, repoSlug, allowedGitEmails, updatedBy, updatedAt, endpoint, emptyText, token, onSaved }) {
const [email, setEmail] = useState('');
const [saving, setSaving] = useState(false);
const [status, setStatus] = useState('');
const [allowedEmails, setAllowedEmails] = useState(Array.isArray(allowedGitEmails) ? allowedGitEmails : []);

useEffect(() => {
setAllowedEmails(Array.isArray(allowedGitEmails) ? allowedGitEmails : []);
}, [allowedGitEmails]);

const saveEmails = async (nextEmails) => {
setSaving(true);
setStatus('Saving git access...');
try {
  const response = await axios.put(endpoint, {
    allowedGitEmails: nextEmails
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const savedEntity = response?.data?.plugin || response?.data?.Plugin || response?.data?.workflow || response?.data?.Workflow || {};
  const savedEmails = Array.isArray(savedEntity.allowedGitEmails || savedEntity.AllowedGitEmails)
    ? (savedEntity.allowedGitEmails || savedEntity.AllowedGitEmails)
    : nextEmails;
  setAllowedEmails(savedEmails);
  setStatus(response?.data?.message || response?.data?.Message || 'Git access updated.');
  if (onSaved) onSaved();
} catch (error) {
  const missing = error?.response?.data?.MissingEmails;
  if (Array.isArray(missing) && missing.length) {
    setStatus(`These emails do not belong to existing users: ${missing.join(', ')}`);
  } else {
    setStatus(error?.response?.data?.Message || error?.response?.data?.message || 'Failed to update git access.');
  }
} finally {
  setSaving(false);
}
};

const addEmail = async () => {
const nextEmail = normalizeEmail(email);
if (!nextEmail) {
  setStatus('Enter an email address first.');
  return;
}
if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
  setStatus('Enter a valid email address.');
  return;
}
await saveEmails(Array.from(new Set([...(allowedEmails || []), nextEmail])).sort());
setEmail('');
};

const removeEmail = async (targetEmail) => {
await saveEmails((allowedEmails || []).filter((value) => normalizeEmail(value) !== normalizeEmail(targetEmail)));
};

const gitRemote = repoSlug ? `${GIT_BASE}/git/${repoSlug}.git` : '';

return (
<div style={styles.gitAccessCard}>
  <div style={styles.gitAccessTitle}>{title}</div>
  <div style={styles.gitAccessText}>{helperText}</div>
  {gitRemote ? (
    <div style={styles.gitRemoteMini}><code style={styles.repoSlug}>{gitRemote}</code></div>
  ) : null}
  <div style={styles.gitAccessRow}>
    <input
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="user@example.com"
      style={styles.gitAccessInput}
    />
    <button onClick={addEmail} disabled={saving} style={styles.smallActionBtn}>
      {saving ? 'Saving...' : 'Add'}
    </button>
  </div>
  {allowedEmails.length > 0 ? (
    <div style={styles.gitAccessList}>
      {allowedEmails.map((value) => (
        <div key={value} style={styles.gitAccessItem}>
          <code style={styles.repoSlug}>{value}</code>
          <button onClick={() => removeEmail(value)} disabled={saving} style={styles.removeMiniBtn}>Remove</button>
        </div>
      ))}
    </div>
  ) : (
    <div style={styles.gitAccessEmpty}>{emptyText}</div>
  )}
  {updatedAt ? (
    <div style={styles.gitAccessAudit}>
      Updated by {updatedBy || 'unknown'} on {new Date(updatedAt).toLocaleString()}
    </div>
  ) : null}
  {status ? <div style={styles.gitAccessStatus}>{status}</div> : null}
</div>
);
}

function UploadTab({ token, onSuccess }) {
const [file, setFile] = useState(null);
const [mediaFiles, setMediaFiles] = useState([]);
const [uploading, setUploading] = useState(false);
const [isPaid, setIsPaid] = useState(false);
const [price, setPrice] = useState('');
const [paymentProvider, setPaymentProvider] = useState('nowpayments');
const [payCurrency, setPayCurrency] = useState('btc');
const [paymentOptions, setPaymentOptions] = useState({ providers: [], payCurrencies: ['btc', 'eth'] });
const [termsOfSale, setTermsOfSale] = useState(DEFAULT_PLUGIN_TERMS);
const [licenseAgreement, setLicenseAgreement] = useState('');
const [downloadAccessDurationMonths, setDownloadAccessDurationMonths] = useState('');

useEffect(() => {
  axios.get(`${API_BASE}/marketplace/providers`).then(response => {
    const data = response.data || {};
    setPaymentOptions(data);
    setPaymentProvider(data.defaultProvider || 'nowpayments');
    setPayCurrency(data.defaultPayCurrency || 'btc');
  }).catch(() => {});
}, []);

const handleUpload = async (e) => {
e.preventDefault();

if (!file) {
  alert('Please select a git archive file');
  return;
}

const formData = new FormData();
formData.append('gitArchive', file);
mediaFiles.forEach(media => formData.append('mediaFiles', media));
formData.append('isPaid', String(isPaid));
formData.append('price', isPaid ? price : '0');
formData.append('currency', 'USD');
formData.append('paymentProvider', paymentProvider);
formData.append('payCurrency', payCurrency);
formData.append('termsOfSale', termsOfSale || DEFAULT_PLUGIN_TERMS);
formData.append('licenseAgreement', licenseAgreement);
formData.append('downloadAccessDurationMonths', downloadAccessDurationMonths);

setUploading(true);
try {
  await axios.post(`${API_BASE}/plugin/upload`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  alert('Plugin uploaded successfully! It will be reviewed by our team.');
  setFile(null);
  setMediaFiles([]);
  if (onSuccess) onSuccess();
} catch (error) {
  const responseData = error.response?.data || {};
  const validationErrors = Array.isArray(responseData.errors) ? responseData.errors : [];
  const invalidFiles = Array.isArray(responseData.invalidFiles) ? responseData.invalidFiles : [];
  const errorMsg = responseData.message || responseData.Message ||
    (validationErrors.length ? validationErrors.join('; ') : '') ||
    (invalidFiles.length ? `Invalid files: ${invalidFiles.join(', ')}` : '') ||
    'Upload failed';
  alert(errorMsg);
  if (validationErrors.length || invalidFiles.length) {
    console.error('Upload validation errors:', { errors: validationErrors, invalidFiles });
  }
}
setUploading(false);

};

return (
<div style={styles.tabContent}>
<h2>Upload New Plugin</h2>

  <form onSubmit={handleUpload} style={styles.uploadForm}>
    <div style={styles.formGroup}>
      <label style={styles.label}>Git Archive (ZIP)</label>
      <input
        type="file"
        accept=".zip"
        onChange={(e) => setFile(e.target.files[0])}
        style={styles.fileInput}
      />
      {file && <span style={styles.fileName}>Selected: {file.name}</span>}
    </div>

    <div style={styles.formGroup}>
      <label style={styles.label}>
        <input type="checkbox" checked={isPaid} onChange={(event) => setIsPaid(event.target.checked)} />{' '}
        Sell this plugin
      </label>
    </div>

    {isPaid && <>
      <div style={styles.formGroup}>
        <label style={styles.label}>Price (USD)</label>
        <input type="number" min="0.50" max="10000" step="0.01" required value={price} onChange={(event) => setPrice(event.target.value)} style={styles.input} />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Payment Provider</label>
        <select value={paymentProvider} onChange={(event) => setPaymentProvider(event.target.value)} style={styles.select}>
          {(paymentOptions.providers || []).filter(item => item.enabled && item.configured).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </div>
      {paymentProvider === 'nowpayments' && <div style={styles.formGroup}>
        <label style={styles.label}>Default Pay Cryptocurrency</label>
        <select value={payCurrency} onChange={(event) => setPayCurrency(event.target.value)} style={styles.select}>
          {(paymentOptions.payCurrencies || []).map(currency => <option key={currency} value={currency}>{currency.toUpperCase()}</option>)}
        </select>
        <div style={styles.infoBox}>Buyers see a live estimate. The exact BTC or ETH amount is fixed by NOWPayments when checkout starts.</div>
      </div>}
      <div style={styles.formGroup}>
        <label style={styles.label}>Download Duration</label>
        <select value={downloadAccessDurationMonths} onChange={(event) => setDownloadAccessDurationMonths(event.target.value)} style={styles.select}>
          <option value="">---</option>
          <option value="6">6 months</option>
          <option value="12">1 year</option>
        </select>
        <div style={styles.infoBox}>Choose how long a buyer can download this plugin after purchase. --- means no duration limit.</div>
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Terms of Sale</label>
        <textarea rows={5} value={termsOfSale} onChange={(event) => setTermsOfSale(event.target.value)} style={styles.textarea} />
        <button type="button" onClick={() => setTermsOfSale(DEFAULT_PLUGIN_TERMS)} style={styles.secondaryBtn}>Use Default Terms</button>
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Plugin License Agreement</label>
        <textarea rows={8} value={licenseAgreement} onChange={(event) => setLicenseAgreement(event.target.value)} style={styles.textarea} placeholder="Enter the license terms buyers must accept." />
      </div>
    </>}

    <div style={styles.formGroup}>
      <label style={styles.label}>Media Files (Images/Videos - Optional)</label>
      <input
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => setMediaFiles(Array.from(e.target.files))}
        style={styles.fileInput}
      />
      {mediaFiles.length > 0 && (
        <span style={styles.fileName}>{mediaFiles.length} file(s) selected</span>
      )}
    </div>

    <div style={styles.infoBox}>
      <strong>Important:</strong>
      <ul style={{marginTop: '10px', paddingLeft: '20px'}}>
        <li>Your archive must include a README.txt file.</li>
        <li>Only approved file types are allowed.</li>
        <li>All files will be scanned for malware.</li>
        <li>Review the tutorial for proper formatting.</li>
      </ul>
    </div>

    <button
      type="submit"
      disabled={uploading}
      style={{...styles.primaryBtn, opacity: uploading ? 0.6 : 1}}
    >
      {uploading ? 'Uploading...' : 'Upload Plugin'}
    </button>
  </form>
</div>

);
}

function WorkflowUploadTab({ token, onSuccess }) {
const [file, setFile] = useState(null);
const [uploading, setUploading] = useState(false);

const handleUpload = async (e) => {
e.preventDefault();

if (!file) {
  alert('Please select a workflow archive file');
  return;
}

const formData = new FormData();
formData.append('gitArchive', file);

setUploading(true);
try {
  await axios.post(`${API_BASE}/workflow/upload`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  alert('Workflow uploaded successfully! It will be reviewed by our team.');
  setFile(null);
  if (onSuccess) onSuccess();
} catch (error) {
  const errorMsg = error.response?.data?.message || 'Upload failed';
  alert(errorMsg);
  if (error.response?.data?.errors) {
    console.error('Validation errors:', error.response.data.errors);
  }
}
setUploading(false);

};

return (
<div style={styles.tabContent}>
<h2>Upload New Workflow</h2>

  <form onSubmit={handleUpload} style={styles.uploadForm}>
    <div style={styles.formGroup}>
      <label style={styles.label}>Workflow Archive (ZIP)</label>
      <input
        type="file"
        accept=".zip"
        onChange={(e) => setFile(e.target.files[0])}
        style={styles.fileInput}
      />
      {file && <span style={styles.fileName}>Selected: {file.name}</span>}
    </div>

    <div style={styles.infoBox}>
      <strong>Important:</strong>
      <ul style={{marginTop: '10px', paddingLeft: '20px'}}>
        <li>Plain exports may contain a workflow JSON, optional images/ folder, and optional example text files.</li>
        <li>Bundle exports may include agent_flow_manifest.json or manifest.json, the workflow JSON, README, skills, images, and examples.</li>
        <li>The workflow name, description, nodes, subflows, and referenced skills are parsed automatically.</li>
        <li>Optional screenshots and example sections are displayed only when supplied.</li>
      </ul>
    </div>

    <button
      type="submit"
      disabled={uploading}
      style={{...styles.primaryBtn, opacity: uploading ? 0.6 : 1}}
    >
      {uploading ? 'Uploading...' : 'Upload Workflow'}
    </button>
  </form>
</div>

);
}

function TutorialTab() {
return (
<div style={styles.tabContent}>
<h2>📚 Upload Tutorial</h2>

  <div style={styles.tutorial}>
    <section style={styles.tutorialSection}>
      <h3>Step 1: Prepare Your Package</h3>
      <p>Create a folder for your plugin or workflow package with:</p>
      <ul>
        <li>Your exported workflow files or plugin source files</li>
        <li>A `README.txt` or `README.md` file at the repo root</li>
        <li>Any additional resources</li>
      </ul>
    </section>

    <section style={styles.tutorialSection}>
      <h3>Step 2: Create README.txt</h3>
      <p>The parser reads a top summary block first, then optional named sections with `== Section Name ==` headers.</p>
      <pre style={styles.codeBlock}>
{`=== My Workflow or Plugin Name ===
Contributors: Your Name
Type: workflow, automation
Tags: agent flow, spreadsheet, reporting
Version: 1.0.0
License: Apache 2.0

One short summary paragraph that explains what this package does.

== Description ==
Explain the workflow or plugin in more detail here.

== Installation ==
List install or import steps here.

== Requirements ==
List any requirements or dependencies here.

== Screenshots ==
1. screenshots/overview.png
Main dashboard or workflow overview
2. screenshots/detail.png
Optional secondary caption

== Frequently Asked Questions ==
= What does this package do? =
Answer the question here.

== Changelog ==
= 1.0.0 =
Initial release notes here.`}
</pre>
      <p>Supported metadata keys in the top block include `Contributors`, `Type`, `Tags`, `Requires At Least`, `Tested Up To`, `Stable Tag`, `Version`, `License`, and `Description`.</p>
    </section>

    <section style={styles.tutorialSection}>
      <h3>Step 3: Create Git Archive</h3>
      <p><strong>Option A: Using Git Command Line</strong></p>
      <pre style={styles.codeBlock}>

{`# Initialize git repository
git init

# Add all files

git add .

# Commit changes

git commit -m "Initial commit"

# Create archive

git archive -format=zip -output=my-plugin.zip HEAD`}
</pre>

      <p><strong>Option B: Manual ZIP</strong></p>
      <p>Simply compress your plugin folder into a .zip file ensuring all files maintain their structure.</p>
    </section>

    <section style={styles.tutorialSection}>
      <h3>Step 4: Upload It</h3>
      <ol>
        <li>Use "Upload Plugin" for plugin repos or "Upload Workflow" for exported workflow repos</li>
        <li>Select your .zip archive</li>
        <li>Optionally add screenshots or demo videos for plugins</li>
        <li>Click the upload button</li>
        <li>Wait for admin approval</li>
      </ol>
    </section>

    <section style={styles.tutorialSection}>
      <h3>Step 4b: Push with Git (New)</h3>
      <p>You can push updates directly to your repo using your account credentials.</p>
      <ul>
        <li>Repo slug format: <code>&lt;plugin-name&gt;-&lt;id&gt;</code></li>
        <li>Use your username/email and password when Git prompts you.</li>
      </ul>
      <pre style={styles.codeBlock}>
{`git init
git remote add origin ${GIT_BASE}/git/<repo-slug>.git
git add .
git commit -m "Initial import"
git push -u origin main`}
      </pre>
      <p>Clone an existing repo:</p>
      <pre style={styles.codeBlock}>
{`git clone ${GIT_BASE}/git/<repo-slug>.git`}
      </pre>
    </section>

    <section style={styles.tutorialSection}>
      <h3>Step 5: Update Your Package</h3>
      <p>To update an existing plugin or workflow:</p>
      <ol>
        <li>Update your plugin files and version in README.txt</li>
        <li>Create a new git archive</li>
        <li>Upload as a new version or push to the repo slug with git</li>
      </ol>
    </section>

    <section style={styles.tutorialSection}>
      <h3>📋 Allowed File Types</h3>
      <p>Check with administrators for current allowed extensions. Common types include:</p>
      <ul>
        <li>.py - Python files</li>
        <li>.js - JavaScript files</li>
        <li>.txt - Text files</li>
        <li>.md - Markdown files</li>
        <li>.json - Configuration files</li>
      </ul>
    </section>

    <section style={styles.tutorialSection}>
      <h3>✅ Best Practices</h3>
      <ul>
        <li>Keep the top metadata block short and accurate</li>
        <li>Use section headers exactly like `== Description ==` when you need parsed sections</li>
        <li>Use semantic versioning (e.g., 1.0.0)</li>
        <li>Include meaningful tags for better discoverability</li>
        <li>Add screenshots/videos to showcase your plugin</li>
        <li>Test your plugin before uploading</li>
        <li>Follow coding best practices</li>
      </ul>
    </section>

    <section style={styles.tutorialSection}>
      <h3>❌ Common Mistakes</h3>
      <ul>
        <li>Missing README.txt or README.md file</li>
        <li>Putting section titles without the `== Section ==` format</li>
        <li>Putting screenshot captions before the numbered screenshot line</li>
        <li>Including disallowed file types</li>
        <li>Very large file sizes</li>
        <li>Malicious or unsafe code</li>
      </ul>
    </section>
  </div>
</div>

);
}

const styles = {
container: {
minHeight: '100vh',
background: '#f5f6fa',
fontFamily: 'Arial, sans-serif'
},
navbar: {
background: 'white',
padding: '20px 40px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center'
},
logo: {
fontSize: '24px',
fontWeight: 'bold',
color: '#667eea',
margin: 0
},
logoLink: {
display: 'inline-block',
textDecoration: 'none'
},
navRight: {
display: 'flex',
alignItems: 'center',
gap: '20px'
},
userName: {
color: '#666'
},
logoutBtn: {
padding: '8px 16px',
background: '#e74c3c',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer'
},
content: {
display: 'flex',
maxWidth: '1400px',
margin: '0 auto',
padding: '20px'
},
sidebar: {
width: '250px',
background: 'white',
padding: '20px',
borderRadius: '8px',
height: 'fit-content',
marginRight: '20px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
},
sidebarBtn: {
width: '100%',
padding: '12px 16px',
marginBottom: '10px',
background: 'transparent',
border: 'none',
borderRadius: '6px',
textAlign: 'left',
cursor: 'pointer',
fontSize: '15px',
transition: 'background 0.2s'
},
activeSidebarBtn: {
background: '#667eea',
color: 'white',
fontWeight: '600'
},
main: {
flex: 1,
background: 'white',
padding: '30px',
borderRadius: '8px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
},
tabContent: {
maxWidth: '800px'
},
sectionTitle: {
margin: '0 0 8px',
color: '#1f2937'
},
sectionDescription: {
margin: '0 0 20px',
color: '#64748b',
lineHeight: 1.5
},
errorMessage: {
padding: '12px 14px',
background: '#fef2f2',
border: '1px solid #fecaca',
borderRadius: '8px',
color: '#991b1b',
marginBottom: '14px'
},
successMessage: {
padding: '12px 14px',
background: '#ecfdf5',
border: '1px solid #a7f3d0',
borderRadius: '8px',
color: '#065f46',
marginBottom: '14px'
},
contributingList: {
display: 'grid',
gap: '14px',
marginTop: '18px'
},
contributingCard: {
padding: '18px',
border: '1px solid #e2e8f0',
borderRadius: '10px',
background: '#fff'
},
contributingHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
gap: '16px',
flexWrap: 'wrap'
},
contributingType: {
fontSize: '11px',
fontWeight: '700',
letterSpacing: '0.08em',
textTransform: 'uppercase',
color: '#667eea'
},
contributingName: {
margin: '4px 0 0',
fontSize: '19px',
color: '#1f2937'
},
contributingDescription: {
margin: '12px 0',
color: '#475569'
},
contributingUrl: {
display: 'block',
padding: '10px',
borderRadius: '6px',
background: '#f1f5f9',
color: '#334155',
overflowWrap: 'anywhere',
fontSize: '12px'
},
contributingHint: {
margin: '10px 0 0',
fontSize: '12px',
color: '#64748b'
},
removeContributionBtn: {
padding: '8px 12px',
border: '1px solid #fecaca',
borderRadius: '6px',
background: '#fff1f2',
color: '#b91c1c',
cursor: 'pointer',
fontWeight: '600'
},
tabHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '20px'
},
headerActions: {
display: 'flex',
alignItems: 'center',
gap: '10px'
},
uploadShortcutBtn: {
padding: '8px 16px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '600'
},
refreshBtn: {
padding: '8px 16px',
background: '#3498db',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer'
},
modalOverlay: {
position: 'fixed',
inset: 0,
zIndex: 2000,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '24px',
background: 'rgba(15, 23, 42, 0.68)'
},
uploadModal: {
position: 'relative',
width: 'min(860px, 100%)',
maxHeight: 'calc(100vh - 48px)',
overflowY: 'auto',
padding: '30px',
background: '#ffffff',
borderRadius: '12px',
boxShadow: '0 24px 70px rgba(15, 23, 42, 0.35)'
},
modalCloseBtn: {
position: 'absolute',
top: '14px',
right: '14px',
zIndex: 1,
width: '36px',
height: '36px',
padding: 0,
border: 'none',
borderRadius: '50%',
background: '#eef2f7',
color: '#334155',
fontSize: '25px',
lineHeight: 1,
cursor: 'pointer'
},
profileForm: {
marginTop: '20px'
},
formGroup: {
marginBottom: '20px'
},
label: {
display: 'block',
marginBottom: '8px',
fontWeight: '600',
color: '#333'
},
input: {
width: '100%',
padding: '12px',
border: '2px solid #e1e8ed',
borderRadius: '6px',
fontSize: '15px'
},
select: {
width: '100%',
padding: '12px',
border: '2px solid #e1e8ed',
borderRadius: '6px',
fontSize: '15px',
background: 'white',
color: '#1f2937'
},
textarea: {
width: '100%',
padding: '12px',
border: '2px solid #e1e8ed',
borderRadius: '6px',
fontSize: '14px',
fontFamily: 'inherit',
resize: 'vertical',
boxSizing: 'border-box'
},
inputReadOnly: {
background: '#f8fafc',
color: '#64748b',
cursor: 'text'
},
formActions: {
display: 'flex',
gap: '10px',
marginTop: '20px'
},
primaryBtn: {
padding: '12px 24px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '600'
},
secondaryBtn: {
padding: '12px 24px',
background: '#95a5a6',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer'
},
pluginsList: {
display: 'flex',
flexDirection: 'column',
gap: '15px'
},
pluginItem: {
border: '1px solid #ddd',
borderRadius: '8px',
padding: '20px',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
gap: '20px'
},
pluginInfo: {
flex: 1,
minWidth: 0
},
pluginDesc: {
color: '#666',
fontSize: '14px',
margin: '10px 0'
},
pluginMeta: {
display: 'flex',
gap: '20px',
fontSize: '13px',
color: '#999',
flexWrap: 'wrap'
},
salesCard: {
marginTop: '16px',
padding: '14px',
border: '1px solid #c7d2fe',
borderRadius: '8px',
background: '#f5f7ff'
},
salesHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
gap: '12px',
flexWrap: 'wrap'
},
salesForm: {
marginTop: '14px',
paddingTop: '14px',
borderTop: '1px solid #dbe2ff'
},
salesGrid: {
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
gap: '14px',
marginBottom: '18px'
},
repoSlug: {
padding: '2px 6px',
background: '#f0f3f7',
borderRadius: '4px',
fontFamily: 'Consolas, monospace',
fontSize: '12px'
},
gitAccessCard: {
marginTop: '16px',
padding: '14px',
border: '1px solid #e5e7eb',
borderRadius: '8px',
background: '#fafbff'
},
gitAccessTitle: {
fontWeight: '700',
color: '#1f2937',
marginBottom: '6px'
},
gitAccessText: {
fontSize: '13px',
color: '#6b7280',
marginBottom: '10px'
},
gitRemoteMini: {
marginBottom: '10px'
},
gitAccessRow: {
display: 'flex',
gap: '10px',
flexWrap: 'wrap',
alignItems: 'center'
},
gitAccessInput: {
flex: '1 1 240px',
padding: '10px 12px',
border: '1px solid #d1d5db',
borderRadius: '6px'
},
smallActionBtn: {
padding: '10px 14px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '600'
},
gitAccessList: {
display: 'flex',
flexDirection: 'column',
gap: '8px',
marginTop: '12px'
},
gitAccessItem: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
gap: '10px',
padding: '10px 12px',
border: '1px solid #e5e7eb',
borderRadius: '6px',
background: 'white',
flexWrap: 'wrap'
},
removeMiniBtn: {
padding: '8px 12px',
background: '#fff1f2',
color: '#b91c1c',
border: '1px solid #fecdd3',
borderRadius: '6px',
cursor: 'pointer'
},
gitAccessEmpty: {
marginTop: '12px',
fontSize: '13px',
color: '#6b7280'
},
gitAccessAudit: {
marginTop: '10px',
fontSize: '12px',
color: '#6b7280'
},
gitAccessStatus: {
marginTop: '10px',
fontSize: '13px',
color: '#374151'
},
pluginActions: {
display: 'flex',
flexDirection: 'column',
gap: '10px',
alignItems: 'flex-end'
},
badge: {
padding: '6px 12px',
borderRadius: '20px',
color: 'white',
fontSize: '12px',
fontWeight: '600'
},
deleteBtn: {
padding: '8px 16px',
background: '#e74c3c',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: '13px'
},
uploadForm: {
marginTop: '20px'
},
fileInput: {
display: 'block',
marginTop: '8px'
},
fileName: {
display: 'block',
marginTop: '8px',
color: '#666',
fontSize: '14px'
},
infoBox: {
background: '#fff3cd',
border: '1px solid #ffc107',
borderRadius: '6px',
padding: '15px',
marginTop: '20px',
fontSize: '14px'
},
referralCard: {
padding: '16px',
margin: '16px 0',
background: '#eefbf3',
border: '1px solid #a7e3bd',
borderRadius: '8px',
color: '#1f5132'
},
referralText: {
margin: '8px 0 14px',
lineHeight: '1.5'
},
referralBtn: {
display: 'inline-block',
padding: '10px 16px',
background: '#27ae60',
color: 'white',
borderRadius: '6px',
textDecoration: 'none',
fontWeight: '700'
},
ordersList: {
display: 'grid',
gap: '14px',
marginTop: '18px'
},
orderCard: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
gap: '20px',
flexWrap: 'wrap',
padding: '18px',
border: '1px solid #e2e8f0',
borderRadius: '10px',
background: '#fff'
},
orderMain: {
flex: '1 1 320px'
},
orderType: {
fontSize: '12px',
fontWeight: '700',
letterSpacing: '0.08em',
textTransform: 'uppercase',
color: '#667eea',
marginBottom: '5px'
},
orderTitle: {
fontSize: '18px',
fontWeight: '700',
color: '#1f2937',
textDecoration: 'none'
},
orderMeta: {
fontSize: '13px',
color: '#64748b',
marginTop: '6px'
},
orderSummary: {
display: 'flex',
flexDirection: 'column',
alignItems: 'flex-end',
gap: '9px'
},
orderStatus: {
padding: '4px 9px',
borderRadius: '999px',
fontSize: '12px',
fontWeight: '700',
textTransform: 'capitalize'
},
orderStatusActive: {
background: '#dcfce7',
color: '#166534'
},
orderStatusExpired: {
background: '#f1f5f9',
color: '#64748b'
},
errorBox: {
padding: '14px',
background: '#fef2f2',
border: '1px solid #fecaca',
borderRadius: '8px',
color: '#991b1b'
},
emptyState: {
textAlign: 'center',
padding: '60px 20px',
color: '#999'
},
loading: {
textAlign: 'center',
padding: '40px',
color: '#999'
},
tutorial: {
marginTop: '20px'
},
tutorialSection: {
marginBottom: '30px',
paddingBottom: '20px',
borderBottom: '1px solid #eee'
},
codeBlock: {
background: '#2c3e50',
color: '#ecf0f1',
padding: '15px',
borderRadius: '6px',
overflow: 'auto',
fontSize: '13px',
lineHeight: '1.6'
}
};

export default UserDashboard;
